// Header logo selection — pure, unit-tested, and SHARED by the live tenant header
// (apps/tenant-sites: <Navigation> / <Header>) and the studio-preview header (NavBlock) so the
// preview always mirrors live.
//
// Field mapping (brand → tenant config), do NOT get these backwards:
//   primary  = logo_primary_url = the DARK logo (dark ink)  → shown on a LIGHT background
//   dark     = logo_dark_url    = the LIGHT / reversed logo  → shown on a DARK background
//   icon     = logo_icon_url    = the favicon / app icon (not used in the header)
//
// The header must pick the logo that CONTRASTS its current background. Historically both headers
// chose the logo from scroll state alone, ASSUMING a transparent-over-hero header is always over a
// dark background — so a LIGHT hero (a light brand color with no image/scrim) rendered the light
// reversed logo on a light background (invisible). These helpers derive the ACTUAL background
// darkness instead.

import { cssColor } from './theme';
import { colorToHex } from './colorHex';

export interface HeaderLogoVariants {
  /** logo_primary_url — the DARK logo (dark ink), designed for LIGHT backgrounds. */
  primary?: string | null;
  /** logo_dark_url — the LIGHT / reversed logo (light ink), designed for DARK backgrounds. */
  dark?: string | null;
  /** logo_icon_url — the favicon / app icon. Not used in the header; here for completeness. */
  icon?: string | null;
}

/**
 * Pick the logo whose ink CONTRASTS the header background:
 *   - dark  background → the LIGHT / reversed logo (`dark`  = logo_dark_url)
 *   - light background → the DARK logo            (`primary` = logo_primary_url)
 * Falls back to the other variant when the preferred one is missing, so a brand that only uploaded
 * one logo still renders it in both states. Returns null only when NO variant exists.
 */
export function pickHeaderLogo(headerIsDark: boolean, logos: HeaderLogoVariants): string | null {
  const primary = logos.primary || null; // dark logo → for a light bg
  const reversed = logos.dark || null; // light/reversed logo → for a dark bg
  if (headerIsDark) return reversed ?? primary;
  return primary ?? reversed;
}

/** WCAG relative luminance (0 = black, 1 = white) of a #rgb / #rrggbb hex, or null if unparseable. */
export function hexLuminance(hex: string | null | undefined): number | null {
  const h = (hex || '').replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const channel = (i: number) => {
    const c = parseInt(full.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

// Luminance below this reads as a "dark" background (matches lib/colorContrast in tenant-sites).
const DARK_BG_THRESHOLD = 0.42;

/**
 * Is the hero sitting behind a transparent header dark?
 *   - a hero with a background IMAGE carries a dark scrim + white hero text by design → dark
 *   - a hero with no image paints the brand primary color → derive darkness from its luminance
 * Unknown / unparseable colors default to DARK — the historically-safe choice that keeps the light
 * reversed logo + white nav text, so this only ever FLIPS behavior for a genuinely light hero.
 */
export function isHeroDark(input: { heroImageUrl?: string | null; primaryColor?: string | null }): boolean {
  if (input.heroImageUrl && input.heroImageUrl.trim()) return true;
  const L = hexLuminance(input.primaryColor);
  return L === null ? true : L < DARK_BG_THRESHOLD;
}

/**
 * Whether the header background is dark right now.
 *   - a SOLID header (scrolled, or any non-overlay bar) is the opaque LIGHT bar → light
 *   - a TRANSPARENT header sits over the hero → matches the hero's darkness
 */
export function headerBackgroundIsDark(input: { isSolid: boolean; heroIsDark: boolean }): boolean {
  return input.isSolid ? false : input.heroIsDark;
}

/**
 * The header's custom colours (plan E1), resolved for rendering — shared by the live headers
 * (tenant-sites Navigation + sgr/Header, through getHeaderScrollTheme) and the preview's NavBlock.
 *
 * - `background`: the nav block's `backgroundColor`, sanitized (cssColor). Setting it makes the
 *   header SOLID in that colour at all times — "make the header navy" must be visible at the top
 *   of the page, not only after scrolling.
 * - `isDark`: whether that background is dark — luminance of any CSS colour cssColor accepts
 *   (hex, rgb()/hsl(), a named colour like `navy`, via colorToHex).
 * - `text`: the explicit `textColor`, else the readable default for the background.
 *
 * Null when neither colour is set, so every existing header renders exactly as before.
 */
export function resolveHeaderColors(input: {
  backgroundColor?: string | null;
  textColor?: string | null;
}): { background?: string; text?: string; isDark: boolean } | null {
  const background = cssColor(input.backgroundColor);
  const explicitText = cssColor(input.textColor);
  if (!background && !explicitText) return null;
  // Measured through colorToHex, so a named / rgb() / hsl() colour is judged by what it is — `navy`
  // is dark. Only a colour nothing can measure falls back to the text colour, then to light.
  const bgL = hexLuminance(colorToHex(background));
  const textL = hexLuminance(colorToHex(explicitText));
  const isDark = bgL !== null ? bgL < DARK_BG_THRESHOLD : textL !== null ? textL >= DARK_BG_THRESHOLD : false;
  const text = explicitText ?? (background ? (isDark ? '#FFFFFF' : '#111827') : undefined);
  return { ...(background ? { background } : {}), ...(text ? { text } : {}), isDark };
}

